import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface WorkspaceInfoBasico {
  id: string;
  name: string;
  workspaceCode?: string;
  logoUrl?: string;
}

/** Membresía tal como la devuelve el backend. */
interface MembresiaWorkspace {
  workspaceId: string;
  role: string | null;
  locationIds: string[];
  capabilities: string[];
}

export interface WorkspaceAccesible extends WorkspaceInfoBasico {
  /** Rol del usuario en el módulo de mantenimiento dentro de ese workspace. */
  rol: string | null;
  capacidades: string[];
  /** El último en el que trabajó, para preseleccionarlo. */
  reciente?: boolean;
}

const CLAVE_ULTIMO_WORKSPACE = 'lastWorkspaceId';

@Injectable({ providedIn: 'root' })
export class WorkspaceAccessService {
  private http = inject(HttpClient);

  /**
   * Resuelve en qué workspaces puede trabajar el usuario y con qué rol.
   *
   * Antes se pedía un token por workspace, y cada uno de esos pedidos disparaba
   * dos llamadas a Console desde el backend: con varios workspaces se llegaba
   * al límite de peticiones y todo fallaba con 429. Ahora el backend lo
   * resuelve de una consulta y acá se hace una sola llamada.
   *
   * Los nombres salen de la lista que ya trajo el inicio de sesión, así que no
   * hace falta volver a pedirlos.
   */
  async resolverAccesibles(workspaces: WorkspaceInfoBasico[]): Promise<WorkspaceAccesible[]> {
    try {
      return await this.consultarAccesibles(workspaces);
    } catch {
      // Sin respuesta no se puede saber a cuáles tiene acceso. Devolver la
      // lista completa sería peor: ofrecería workspaces que van a dar 403.
      return [];
    }
  }

  /**
   * Lo mismo, pero si no se pudo consultar lo dice en vez de devolver vacío.
   *
   * El login la necesita así: para él una lista vacía significa "no tiene
   * acceso al módulo". Cuando Console limitaba las peticiones, la consulta
   * fallaba, volvía vacía, y al usuario se le decía que no tenía acceso.
   */
  async consultarAccesibles(workspaces: WorkspaceInfoBasico[]): Promise<WorkspaceAccesible[]> {
    if (workspaces.length === 0) return [];

    const ultimo = this.ultimoWorkspaceId();

    const membresias = await firstValueFrom(
      this.http.get<MembresiaWorkspace[]>(
        `${environment.apiUrl}/api/v1/auth/my-workspaces`,
        { withCredentials: true },
      ),
    );

    const porWorkspace = new Map(membresias.map(m => [m.workspaceId, m]));

    return workspaces
      .filter(ws => porWorkspace.has(ws.id))
      .map(ws => {
        const m = porWorkspace.get(ws.id)!;
        return {
          ...ws,
          rol: m.role ?? null,
          capacidades: m.capabilities ?? [],
          reciente: ws.id === ultimo,
        };
      });
  }

  ultimoWorkspaceId(): string | null {
    return localStorage.getItem(CLAVE_ULTIMO_WORKSPACE);
  }

  recordarUltimo(workspaceId: string): void {
    localStorage.setItem(CLAVE_ULTIMO_WORKSPACE, workspaceId);
  }

  /** El que conviene preseleccionar: el último usado, o el primero. */
  preseleccionado(workspaces: WorkspaceAccesible[]): WorkspaceAccesible | null {
    if (!workspaces.length) return null;
    const ultimo = this.ultimoWorkspaceId();
    return workspaces.find(w => w.id === ultimo) ?? workspaces[0];
  }
}
